import type { AuthTokens } from '@/types/user'

/**
 * Token persistence, kept in its own module so that both the HTTP layer and the
 * auth store can reach it without importing each other.
 *
 * Tokens live in localStorage: the backend hands them over in the JSON body
 * (not as an httpOnly cookie), so there is no more protected place available to
 * a pure SPA. The trade-off is XSS exposure, mitigated by three things, in
 * descending order of importance: the CSP (`script-src` with no 'unsafe-inline',
 * see netlify.toml), Vue's default escaping plus never using `v-html` on remote
 * data, and the short token lifetimes — 60 minutes for the access token, and a
 * refresh token that the backend revokes as soon as it is spent.
 */
const ACCESS_TOKEN_KEY = 'travick.accessToken'
const REFRESH_TOKEN_KEY = 'travick.refreshToken'

/** localStorage throws in private-mode Safari and when storage is full. */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Non-fatal: the session simply won't survive a reload.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Ignore.
  }
}

export function getAccessToken(): string | null {
  return safeGet(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return safeGet(REFRESH_TOKEN_KEY)
}

export function setTokens(tokens: AuthTokens): void {
  safeSet(ACCESS_TOKEN_KEY, tokens.accessToken)
  safeSet(REFRESH_TOKEN_KEY, tokens.refreshToken)
}

export function clearTokens(): void {
  safeRemove(ACCESS_TOKEN_KEY)
  safeRemove(REFRESH_TOKEN_KEY)
}

export function hasSession(): boolean {
  return getAccessToken() !== null
}
